import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Radio, Wifi, Lightbulb, Blinds, Lock, Thermometer, Brain, Smartphone } from "lucide-react";
import { useLang } from "../context/I18nContext";
import SectionVideo from "../components/SectionVideo";
import { SECTION_VIDEOS } from "../config/sectionVideos";

/**
 * Gebäudeautomation — Bus, Funk, AI, Licht, Beschattung, Zutritt, Sensorik
 */
export default function Gebaeudeautomation() {
  const { t } = useLang();

  const sections = [
    {
      n: "01", icon: Cpu, title: "Gebäudeautomation",
      intro: "Heizung, Kühlung, Licht, Beschattung, Zutritt und Energie — abgestimmt zu einem System. Nicht jedes Gebäude braucht dieselbe Automation. Die Lösung wird an Gebäudegröße und Nutzung angepasst.",
      items: ["Heizung & Kühlung", "Licht & Szenen", "Beschattung", "Zutritt", "Energie-Lastmanagement", "Raumregelung", "Zeitprogramme", "Automationen", "App-Steuerung", "Fernzugriff"],
    },
    {
      n: "02", icon: Radio, title: "Bussysteme",
      intro: "Kabelgebundene Steuerung mit dezentralen Komponenten und zentraler Visualisierung. Konkrete Marken werden nur dort genannt, wo sie tatsächlich angeboten werden.",
      items: ["Dezentrale Komponenten", "Zentrale Visualisierung", "Erweiterbarkeit", "Strukturierte Planung", "Kommunikation technischer Systeme"],
    },
    {
      n: "03", icon: Wifi, title: "Funksysteme",
      intro: "Besonders wichtig für Sanierungen. Funklösungen sind sinnvoll, wenn bestehende Gebäude nicht vollständig neu verkabelt werden sollen.",
      items: ["Funkthermostate", "Raumsensoren", "Schalter & Aktoren", "Beschattungssteuerung", "Lichtsteuerung", "Zutritt", "Heizungsregelung", "Nachrüstung"],
    },
    {
      n: "04", icon: Brain, title: "Intelligente Steuerung",
      intro: "Datenbasierte Funktionen unterstützen den bedarfsgerechten Betrieb. Kein pauschales KI-Versprechen — sondern konkrete Auswertung von Betriebsdaten.",
      items: ["Betriebsdaten auswerten", "Nutzungsmuster erkennen", "Zeitprogramme optimieren", "Verbrauch analysieren", "Abweichungen erkennen", "Wartung unterstützen", "Sollwerte innerhalb Grenzen optimieren"],
    },
    {
      n: "05", icon: Lightbulb, title: "Licht",
      intro: "Innen- und Außenbeleuchtung mit Szenen, Dimmung und Sensorik. Verknüpfung mit Beschattung für ein stimmiges Raumklima.",
      items: ["Innenbeleuchtung", "Außenbeleuchtung", "Lichtszenen", "Dimmung", "Bewegungsmelder", "Präsenzmelder", "Zeitprogramme", "App-Steuerung"],
    },
    {
      n: "06", icon: Blinds, title: "Beschattung",
      intro: "Rollläden und Jalousien mit Zeit- und Sensorsteuerung. Berücksichtigung von Sonnenstand und Innentemperatur.",
      items: ["Rollläden", "Jalousien", "Sonnenschutz", "Zeitsteuerung", "Sensorsteuerung", "Sonnenstand", "Temperaturbezug", "App-Steuerung"],
    },
    {
      n: "07", icon: Lock, title: "Zutritt",
      intro: "Elektronischer Zutritt und Türsteuerung mit Berechtigungsverwaltung. Garantierte Sicherheitsversprechen werden nicht gegeben — die Lösung richtet sich nach Projekt und Anforderung.",
      items: ["Elektronischer Zutritt", "Türsteuerungen", "Zutrittsberechtigungen", "Video-Türkommunikation", "Gegensprechanlagen", "App-Anbindung (wo vorgesehen)"],
    },
    {
      n: "08", icon: Thermometer, title: "Sensorik",
      intro: "Sensoren liefern die Grundlage für automatische Regelungen — ohne präzise Messung keine wirksame Automation.",
      items: ["Temperatur", "Feuchtigkeit", "CO₂", "Helligkeit", "Bewegung", "Präsenz", "Fensterstatus", "Türstatus", "Verbrauch", "Energie"],
    },
  ];

  return (
    <div className="page-shell" data-testid="automation-page">
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">03 · {t("nav.automation")}</div>
        <h1 className="page-title">
          Automation,<br/>
          <span className="italic-accent">die zum Gebäude passt.</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          Bus oder Funk, zentrale oder dezentrale Lösungen, einfache Zeitsteuerung oder vollvernetzte Visualisierung —
          die Automation wird an Gebäude, Nutzung und Budget angepasst.
        </p>
        <div className="mt-8 flex items-center gap-3 text-[#5FA637]">
          <Smartphone size={18}/>
          <span className="text-sm uppercase tracking-[0.15em] font-semibold">App · Visualisierung · Fernzugriff</span>
        </div>
      </section>

      {/* Section video — Automation */}
      <SectionVideo {...SECTION_VIDEOS.gebaeudeautomation}/>

      {sections.map((sec) => (
        <section key={sec.n} className="site-container section-block border-t border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5 text-[#5FA637]">{sec.n}</div>
              <div className="flex items-center gap-3 mb-5">
                <sec.icon size={24} strokeWidth={1.5} className="text-[#5FA637]"/>
                <h2 className="section-title">{sec.title}</h2>
              </div>
              <p className="text-white/70 leading-relaxed">{sec.intro}</p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {sec.items.map((it, i) => (
                  <div key={i} className="bg-[#0A1F38]/65 px-4 py-3 text-sm text-white/80 flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#5FA637] shrink-0"/>
                    {it}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="site-container section-block border-t border-white/10">
        <div className="cta-panel">
          <div className="eyebrow mb-5 text-[#5FA637]">Nächster Schritt</div>
          <h2 className="section-title max-w-3xl">
            Automation <span className="italic-accent">planen.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" className="btn-primary">
              {t("cta.inquiry")} <ArrowRight size={14}/>
            </Link>
            <Link to="/wohnanlagen" className="btn-ghost">
              {t("nav.residential")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
